import React, {useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate, useParams} from "react-router-dom";
import PageLayout from "../PageLayout";
import Btn from "../../ui/Btn";
import Select from "../../ui/Select";
import appStore from "../../storage/AppStore";
import clubStore from "../../storage/ClubStore";
import systemStore from "../../storage/SystemStore";
import "../../common/game.css";

const safeArray = (v) => Array.isArray(v) ? v : [];

const getTournamentTeamId = (item) =>
  item?.tournament_team_id || item?.id || null;

const getGameTeamId = (item) =>
  item?.game_team_id || item?.game_team?.id || item?.id || null;

const getTeamName = (item) =>
  item?.game_team?.name || item?.name || "-";

const getDivisionId = (item) =>
  item?.division_id || item?.division?.id || null;

const getDivisionName = (item) =>
  item?.division?.name || appStore.tr.no_division || "No Category";

const getGroupId = (item) =>
  item?.group_id || item?.group?.id || null;

const getGroupName = (item) =>
  item?.group?.name || appStore.tr.no_group || "No Group";

const getMembersCount = (item) => {
  if (typeof item?.members_count === "number") return item.members_count;
  if (typeof item?.game_team?.members_count === "number") return item.game_team.members_count;
  if (Array.isArray(item?.members)) return item.members.length;
  if (Array.isArray(item?.game_team?.members)) return item.game_team.members.length;
  return 0;
};

const getGamesCount = (item) => {
  if (typeof item?.games_count === "number") return item.games_count;
  if (typeof item?.game_team?.games_count === "number") return item.game_team.games_count;
  if (Array.isArray(item?.games)) return item.games.length;
  if (Array.isArray(item?.game_team?.games)) return item.game_team.games.length;
  return 0;
};

const CompetitionGroups = ({role = "MGR"}) => {
  const navigate = useNavigate();
  const {id} = useParams();

  const basePath = role === "TRN" ? "/trainer" : "/manager";

  const [savingByTournamentTeamId, setSavingByTournamentTeamId] = useState({});

  useEffect(() => {
    if (!id) return;
    clubStore.tournament.getCompetition(id).catch(console.error);
    clubStore.tournament.getTournamentDivisions(id).catch(console.error);
  }, [id]);

  const competition = clubStore.tournament.competition || {};
  const tournamentTeams = safeArray(competition?.teams);
  const divisions = safeArray(clubStore.tournament.divisions || competition?.divisions || []);

  const isOwner =
    Number(competition?.company_id) === Number(clubStore.club?.id) ||
    !!competition?.is_owner;

  const teamsWithoutDivision = useMemo(() => {
    return tournamentTeams.filter((x) => !getDivisionId(x));
  }, [tournamentTeams]);

  const divisionsWithTeams = useMemo(() => {
    return divisions.map((division) => {
      const teams = tournamentTeams.filter((x) => Number(getDivisionId(x)) === Number(division.id));
      return {
        ...division,
        teams,
      };
    }).filter((x) => x.teams.length > 0);
  }, [divisions, tournamentTeams]);

  const getGroupOptions = (division) => {
    return [
      {
        id: null,
        name: appStore.tr.no_group || "No Group",
      },
      ...safeArray(division?.groups || []),
    ];
  };

  const refreshCompetition = async () => {
    await clubStore.tournament.getCompetition(id);
  };

  const handleGroupChange = async (team, groupId) => {
    const tournamentTeamId = getTournamentTeamId(team);
    if (!tournamentTeamId) return;

    try {
      setSavingByTournamentTeamId((prev) => ({
        ...prev,
        [tournamentTeamId]: true,
      }));

      const ret = await clubStore.tournament.setTournamentTeamGroup(
        Number(tournamentTeamId),
        groupId == null ? null : Number(groupId),
        Number(id)
      );

      if (ret?.result === 0) {
        systemStore.setSuccess(
          appStore.tr.group_assigned || "Group assigned"
        );
        await refreshCompetition();
      }
    } catch (err) {
      systemStore.setError(err?.message || "Save failed");
    } finally {
      setSavingByTournamentTeamId((prev) => ({
        ...prev,
        [tournamentTeamId]: false,
      }));
    }
  };

  const renderTeamCard = (team, division = null) => {
    const tournamentTeamId = getTournamentTeamId(team);
    const gameTeamId = getGameTeamId(team);
    const saving = !!savingByTournamentTeamId[tournamentTeamId];

    return (
      <div key={tournamentTeamId || gameTeamId} className="col-12 lg:col-6">
        <div className="game-card h-full flex flex-column justify-content-between">
          <div>
            <div className="flex align-items-start gap-3 mb-3">
              <span className="badge badge-upcoming flex-shrink-0">
                <i className="pi pi-users" aria-hidden="true" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-lg line-height-3" title={getTeamName(team)}>
                  {getTeamName(team)}
                </div>
              </div>
            </div>

            <div className="game-badges mb-3">
              <span className="badge badge-tournament">
                <i className="pi pi-tag" aria-hidden="true" />
                {appStore.tr.division}: {getDivisionName(team)}
              </span>
              <span className="badge badge-upcoming">
                <i className="pi pi-sitemap" aria-hidden="true" />
                {appStore.tr.group || "Group"}: {getGroupName(team)}
              </span>
            </div>

            {division ? (
              <div className="mb-3">
                <label htmlFor={`group_${tournamentTeamId}`} className="block mb-2 text-600 text-sm font-semibold">
                  {appStore.tr.group || "Group"}
                </label>
                <Select
                  id={`group_${tournamentTeamId}`}
                  value={getGroupId(team)}
                  options={getGroupOptions(division)}
                  itemTemplate={(item) => <>{item?.name || "-"}</>}
                  onChange={(val) => handleGroupChange(team, val)}
                  disabled={saving || systemStore.loading}
                />
              </div>
            ) : (
              <div className="mb-3 text-600 text-sm">
                <i className="pi pi-info-circle mr-2" aria-hidden="true" />
                {appStore.tr.no_division || "No Category"}
              </div>
            )}

            <div className="flex flex-column gap-2">
              <div className="competition-detail-item flex align-items-center justify-content-between gap-2">
                <span className="text-600 text-sm">
                  <i className="pi pi-users mr-2" aria-hidden="true" />
                  {appStore.tr.players || "Players"}
                </span>
                <span className="text-lg font-bold flex-shrink-0">{getMembersCount(team)}</span>
              </div>
              <div className="competition-detail-item flex align-items-center justify-content-between gap-2">
                <span className="text-600 text-sm">
                  <i className="pi pi-calendar mr-2" aria-hidden="true" />
                  {appStore.tr.games || "Games"}
                </span>
                <span className="text-lg font-bold flex-shrink-0">{getGamesCount(team)}</span>
              </div>
            </div>
          </div>

          <div className="game-card-actions flex flex-wrap gap-2 mt-3">
            <Btn
              icon="pi pi-pencil"
              label={appStore.tr.edit || "Edit"}
              outlined
              onClick={() => navigate(`${basePath}/games/game_team/${gameTeamId}`)}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageLayout
      header={
        <div className="flex align-items-center gap-3 w-full min-w-0">
          <i className="pi pi-sitemap text-primary text-xl" aria-hidden="true" />
          <div className="flex flex-column gap-1 min-w-0">
            <h3 className="m-0">{appStore.tr.competition_groups || "Competition Groups"}</h3>
            <span className="text-600 text-sm">{competition.name}</span>
          </div>
        </div>
      }
    >
      <div className="col-12">
        {!competition?.id ? (
          <div className="game-page p-4 text-center">
            <i className="pi pi-spin pi-spinner text-600 text-xl mb-2" aria-hidden="true" />
            <div className="text-600">{appStore.tr.loading || "Loading..."}</div>
          </div>
        ) : !isOwner ? (
          <div className="game-page p-4 text-center">
            <i className="pi pi-lock text-600 text-xl mb-2" aria-hidden="true" />
            <div className="text-600">{appStore.tr.access_denied || "Access denied"}</div>
          </div>
        ) : (
          <>
            {!!teamsWithoutDivision.length && (
              <section className="mb-4">
                <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <div className="flex align-items-center gap-2 min-w-0">
                    <i className="pi pi-exclamation-circle text-600" aria-hidden="true" />
                    <h4 className="m-0">{appStore.tr.teams_without_category || "Teams Without Category"}</h4>
                  </div>
                  <span className="badge badge-warning">{teamsWithoutDivision.length}</span>
                </div>
                <div className="grid">
                  {teamsWithoutDivision.map((team) => renderTeamCard(team, null))}
                </div>
              </section>
            )}

            {!divisionsWithTeams.length ? (
              <div className="game-page p-4 text-center">
                <i className="pi pi-users text-600 text-xl mb-2" aria-hidden="true" />
                <div className="text-600">
                  {appStore.tr.no_competition_teams || "No competition teams"}
                </div>
              </div>
            ) : (
              divisionsWithTeams.map((division) => (
                <section key={division.id} className="mb-4">
                  <div className="game-page p-3 mb-3">
                    <div className="flex align-items-center justify-content-between flex-wrap gap-3">
                      <div className="flex align-items-center gap-2 min-w-0">
                        <i className="pi pi-tag text-primary" aria-hidden="true" />
                        <h4 className="m-0">{appStore.tr.division}: {division.name}</h4>
                      </div>
                      <div className="game-badges">
                        <span className="badge badge-tournament">
                          <i className="pi pi-sitemap" aria-hidden="true" />
                          {appStore.tr.groups}: {safeArray(division.groups).length}
                        </span>
                        <span className="badge badge-upcoming">
                          <i className="pi pi-users" aria-hidden="true" />
                          {appStore.tr.teams || "Teams"}: {division.teams.length}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="grid">
                    {division.teams.map((team) => renderTeamCard(team, division))}
                  </div>
                </section>
              ))
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default observer(CompetitionGroups);
